import { createContext, useContext, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { content } from '@/data/content';

const LanguageContext = createContext(null);

const SUPPORTED = ['tr', 'en'];

export const LanguageProvider = ({ children }) => {
  const { lang: urlLang } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const lang = SUPPORTED.includes(urlLang) ? urlLang : 'tr';

  useEffect(() => {
    document.documentElement.lang = lang;
    try {
      localStorage.setItem('mads.lang', lang);
    } catch (_e) {
      /* ignore */
    }
  }, [lang]);

  const setLang = (next) => {
    if (!SUPPORTED.includes(next) || next === lang) return;
    // Preserve hash so smooth-scroll anchors keep working
    navigate(`/${next}${location.hash || ''}`);
  };

  const value = {
    lang,
    setLang,
    toggle: () => setLang(lang === 'tr' ? 'en' : 'tr'),
    t: content[lang],
  };

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

export const useLang = () => {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error('useLang must be used within LanguageProvider');
  return ctx;
};

/** Pick preferred language from localStorage → browser → fallback. */
export const detectPreferredLang = () => {
  try {
    const stored = localStorage.getItem('mads.lang');
    if (SUPPORTED.includes(stored)) return stored;
  } catch (_e) {
    /* ignore */
  }
  if (typeof navigator !== 'undefined') {
    const langs = navigator.languages || [navigator.language || 'tr'];
    for (const l of langs) {
      const short = String(l).toLowerCase().split('-')[0];
      if (SUPPORTED.includes(short)) return short;
    }
  }
  return 'tr';
};
